/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	Children,
	isValidElement,
	type ComponentProps,
	type PropsWithChildren,
	type ReactElement,
	type ReactNode,
} from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import {
	DEFAULT_IMAGE_HEIGHT,
	DEFAULT_IMAGE_WIDTH,
	wrappedLayouts,
} from '@atlaskit/editor-common/media-single/constants';
import type {
	Media as OriginalMedia,
	MediaSingle as OriginalMediaSingle,
} from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

type MediaProps = ComponentProps<typeof OriginalMedia> & { nodeType?: string };

const styles = cssMap({
	figure: { maxWidth: '100%' },
	centered: {
		marginTop: token('space.300'),
		marginRight: 'auto',
		marginBottom: token('space.300'),
		marginLeft: 'auto',
	},
	alignStart: {
		marginBlockStart: token('space.300'),
		marginBlockEnd: token('space.300'),
		marginInlineStart: token('space.0'),
		marginInlineEnd: 'auto',
	},
	alignEnd: {
		marginBlockStart: token('space.300'),
		marginBlockEnd: token('space.300'),
		marginInlineStart: 'auto',
		marginInlineEnd: token('space.0'),
	},
	wrapLeft: {
		float: 'left',
		marginTop: token('space.150'),
		marginRight: token('space.150'),
		marginBottom: token('space.150'),
		marginLeft: 'auto',
	},
	wrapRight: {
		float: 'right',
		marginTop: token('space.150'),
		marginRight: 'auto',
		marginBottom: token('space.150'),
		marginLeft: token('space.150'),
	},
	media: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		maxHeight: '100%',
		maxWidth: '100%',
	},
});

const isMediaElement = (node: ReactNode): node is ReactElement<MediaProps> => {
	if (!isValidElement<MediaProps>(node)) {
		return false;
	}

	return node.props.nodeType === 'media' || ['external', 'file', 'link'].includes(node.props.type);
};

const findMediaElement = (node: ReactNode): ReactElement<MediaProps> | null => {
	let current = Children.toArray(node)[0];

	while (current !== undefined) {
		if (isMediaElement(current)) {
			return current;
		}

		if (!isValidElement<PropsWithChildren<unknown>>(current)) {
			return null;
		}

		current = Children.toArray(current.props.children)[0];
	}

	return null;
};

const getMediaDimension = (dimension: number | null | undefined, fallback: number): number =>
	dimension && Number.isFinite(dimension) && dimension > 0 ? dimension : fallback;

type LightMediaSingleProps = ComponentProps<typeof OriginalMediaSingle> & LightNodeProps;

// Light media preserves placement and aspect ratio, not Platform's exact breakout sizing.
const getPlaceholderWidth = (props: LightMediaSingleProps, intrinsicWidth: number): string => {
	if (props.width && Number.isFinite(props.width) && props.width > 0) {
		return props.widthType === 'pixel' ? `${props.width}px` : `${props.width}%`;
	}
	if (props.layout === 'wide' || props.layout === 'full-width') return '100%';
	return wrappedLayouts.includes(props.layout)
		? `min(50%, ${intrinsicWidth}px)`
		: `${intrinsicWidth}px`;
};

export const MediaSingle = (props: LightMediaSingleProps): ReactElement => {
	const [media, caption] = Children.toArray(props.children);
	const mediaElement = findMediaElement(media);
	const mediaWidth = getMediaDimension(mediaElement?.props.width, DEFAULT_IMAGE_WIDTH);
	const mediaHeight = getMediaDimension(mediaElement?.props.height, DEFAULT_IMAGE_HEIGHT);
	const figureWidth = getPlaceholderWidth(props, mediaWidth);
	const isCentered = !wrappedLayouts.includes(props.layout);
	const alt = mediaElement?.props.alt;

	return (
		<figure
			ref={props.lightNodeRef}
			css={[
				styles.figure,
				isCentered && styles.centered,
				props.layout === 'align-start' && styles.alignStart,
				props.layout === 'align-end' && styles.alignEnd,
				props.layout === 'wrap-left' && styles.wrapLeft,
				props.layout === 'wrap-right' && styles.wrapRight,
			]}
			data-layout={props.layout}
			data-node-type="mediaSingle"
			data-renderer-start-pos={props.dataAttributes?.['data-renderer-start-pos']}
			style={{ width: figureWidth }}
		>
			<div
				aria-hidden={alt ? undefined : true}
				aria-label={alt || undefined}
				css={styles.media}
				role={alt ? 'img' : undefined}
				style={{
					aspectRatio: `${mediaWidth} / ${mediaHeight}`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Full figure width reserves the authored media geometry before activation.
					width: '100%',
				}}
			/>
			{props.allowCaptions && caption}
		</figure>
	);
};
